/**
 * CacheService.ts
 * 
 * A utility service for caching API responses to improve performance and reduce API calls.
 * Provides functions for:
 * - Storing responses in cache
 * - Retrieving responses from cache
 * - Checking if a cached response is valid
 * - Clearing the cache
 */

// Type for cache entry
export type CacheEntry<T> = {
  data: T;
  timestamp: number;
  expiresIn: number; // in milliseconds
};

// Type for cache storage
export type CacheStorage = {
  [key: string]: CacheEntry<any>;
};

// Default cache expiration time (30 minutes)
const DEFAULT_CACHE_EXPIRATION = 30 * 60 * 1000;

// Cache storage
let cacheStorage: CacheStorage = {};

/**
 * Generates a cache key from the request parameters
 * @param prefix A prefix to identify the API service
 * @param params The parameters used in the API request
 * @returns A string key for the cache
 */
export const generateCacheKey = (prefix: string, params: any): string => {
  return `${prefix}:${JSON.stringify(params)}`;
};

/**
 * Stores data in the cache
 * @param key The cache key
 * @param data The data to store
 * @param expiresIn Optional expiration time in milliseconds
 */
export const setCache = <T>(key: string, data: T, expiresIn: number = DEFAULT_CACHE_EXPIRATION): void => {
  cacheStorage[key] = {
    data,
    timestamp: Date.now(),
    expiresIn
  };
};

/**
 * Retrieves data from the cache if it exists and is not expired
 * @param key The cache key
 * @returns The cached data or null if not found or expired
 */
export const getCache = <T>(key: string): T | null => {
  const entry = cacheStorage[key] as CacheEntry<T> | undefined;
  
  if (!entry) {
    return null;
  }
  
  // Check if the entry is expired
  if (Date.now() - entry.timestamp > entry.expiresIn) {
    // Remove expired entry
    delete cacheStorage[key];
    return null;
  }
  
  return entry.data;
};

/**
 * Checks if a valid cache entry exists for the given key
 * @param key The cache key
 * @returns True if a valid cache entry exists, false otherwise
 */
export const hasValidCache = (key: string): boolean => {
  return getCache(key) !== null;
};

/**
 * Clears all cache entries
 */
export const clearCache = (): void => {
  cacheStorage = {};
};

/**
 * Clears cache entries with a specific prefix
 * @param prefix The prefix to match
 */
export const clearCacheByPrefix = (prefix: string): void => {
  Object.keys(cacheStorage).forEach(key => {
    if (key.startsWith(`${prefix}:`)) {
      delete cacheStorage[key];
    }
  });
};

/**
 * A higher-order function that wraps an async function with caching
 * @param fn The async function to wrap
 * @param prefix The prefix for the cache key
 * @param expiresIn Optional expiration time in milliseconds
 * @returns A wrapped function that uses caching
 */
export const withCache = <T, P extends any[]>(
  fn: (...args: P) => Promise<T>,
  prefix: string,
  expiresIn: number = DEFAULT_CACHE_EXPIRATION
): ((...args: P) => Promise<T>) => {
  return async (...args: P): Promise<T> => {
    const cacheKey = generateCacheKey(prefix, args);
    const cachedData = getCache<T>(cacheKey);
    
    if (cachedData !== null) {
      return cachedData;
    }
    
    const result = await fn(...args);
    setCache<T>(cacheKey, result, expiresIn);
    return result;
  };
};